import type {
	AnalyticsBrowser,
	MiddlewareFunction,
} from '@segment/analytics-next';

declare global {
	interface Window {
		analytics: AnalyticsBrowser;
	}
}

const hasStoredSegmentUser = () => {
	try {
		if (JSON.parse(localStorage.getItem('ajs_user_id') ?? 'null')) {
			return true;
		}
	} catch {
		// Cookies remain available when localStorage is disabled or malformed.
	}

	try {
		const cookie = document.cookie
			.split(';')
			.map((value) => value.trim())
			.find((value) => value.startsWith('ajs_user_id='));
		return Boolean(
			cookie &&
			JSON.parse(decodeURIComponent(cookie.slice('ajs_user_id='.length))),
		);
	} catch {
		return false;
	}
};

const stripTokenFromUrl = (value: string): string => {
	const queryStart = value.indexOf('?');
	if (queryStart === -1) return value;
	const hashStart = value.indexOf('#', queryStart);
	const queryEnd = hashStart === -1 ? value.length : hashStart;
	const params = new URLSearchParams(value.slice(queryStart + 1, queryEnd));
	const cleanParams = new URLSearchParams();
	let changed = false;

	params.forEach((paramValue, key) => {
		if (key === 'token') {
			changed = true;
			return;
		}
		const cleanValue = stripTokenFromUrl(paramValue);
		changed ||= cleanValue !== paramValue;
		cleanParams.append(key, cleanValue);
	});

	if (!changed) return value;
	const search = cleanParams.toString();
	return `${value.slice(0, queryStart)}${search ? `?${search}` : ''}${value.slice(queryEnd)}`;
};

const stripPageTokens = (page?: Record<string, unknown>) => {
	if (!page) return;
	for (const key of ['url', 'search', 'referrer']) {
		if (typeof page[key] === 'string') {
			page[key] = stripTokenFromUrl(page[key]);
		}
	}
};

const segmentPrivacyMiddleware: MiddlewareFunction = ({payload, next}) => {
	stripPageTokens(payload.obj.context?.page);
	if (payload.obj.type === 'page') {
		stripPageTokens(payload.obj.properties);
	}
	next(payload);
};

export const prepareSegment = (isLoggedOut: boolean) => {
	const analytics = window.analytics;
	if (!analytics) return;
	analytics.addSourceMiddleware(segmentPrivacyMiddleware);

	if (!isLoggedOut || !hasStoredSegmentUser()) return;
	analytics.register({
		name: 'Reset logged-out Segment user',
		type: 'before',
		version: '1.0.0',
		isLoaded: () => true,
		// Plugin loading precedes buffered consent, identify, page and track calls.
		load: async (_context, instance) => {
			instance.reset();
		},
	});
};
